import { Calendar, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Event Date + Location box.
 *
 * Owner-approved copy and formatting (Feb 2026, latest correction):
 *   ENGLISH
 *     • Yellow label: "EVENT DATES"
 *     • White value:  "December 10–12, 2026"
 *     • Yellow label: "LOCATION"
 *     • White value:  "Rimal Center, Exit 17, Riyadh, Kingdom of Saudi Arabia"
 *   ARABIC
 *     • Yellow label: "تاريخ الفعالية"
 *     • White value:  "١٠–١٢ ديسمبر ٢٠٢٦"
 *     • Yellow label: "الموقع"
 *     • White value:  "الرمال سنتر، مخرج ١٧، الرياض، المملكة العربية السعودية"
 *
 * Rules:
 *   • Only the four kicker labels are yellow (#F5C518).
 *   • Every value is the SAME white (#FFFFFF) — no grey, no white/70, no
 *     mixed opacity.
 *   • Arabic uses "Noto Sans Arabic" for BOTH labels and values (no
 *     fallback / decorative / Latinised Arabic renderer). Explicit inline
 *     font-family on every AR text node so no ambient rule can override.
 *   • Arabic-Indic numerals (١٠–١٢, ١٧, ٢٠٢٦) preserved verbatim from the
 *     i18n string; no digit substitution.
 *   • Correct RTL joining and punctuation preserved by using `dir="rtl"`
 *     and `lang="ar"` on the Arabic subtree.
 *   • Comfortably readable across desktop and mobile: base ≥ 15px on
 *     mobile / ≥ 17px on desktop for values; labels a hair smaller
 *     with strong weight so the hierarchy reads at a glance.
 *   • Layout is a compact two-row card so labels and values pair
 *     cleanly and the two mediums (dates, location) never blur into a
 *     single sentence.
 */
export default function EventDateBadge({ className = "" }) {
  const { t, isRTL } = useLanguage();

  // Noto Sans Arabic — enforced inline so no ambient font-family rule
  // (Latin fallback, ambient .font-body, or user-agent style) can leak
  // into the Arabic subtree.
  const AR_FONT = "'Noto Sans Arabic', system-ui, sans-serif";

  // Same white for every value cell — no /70, no /90.
  const VALUE_WHITE = "#FFFFFF";
  const LABEL_YELLOW = "#F5C518";

  const rowClasses =
    "flex items-baseline gap-3 sm:gap-4 justify-center " +
    (isRTL ? "flex-row-reverse" : "flex-row");

  return (
    <div
      data-testid="event-date-badge"
      dir={isRTL ? "rtl" : "ltr"}
      lang={isRTL ? "ar" : "en"}
      className={
        "inline-flex flex-col items-center gap-2 sm:gap-3 " +
        "px-5 py-4 sm:px-6 sm:py-5 " +
        "border border-[#F5C518]/30 bg-[#F5C518]/[0.06] rounded-md " +
        "max-w-full text-center " +
        className
      }
      style={isRTL ? { fontFamily: AR_FONT, letterSpacing: "normal" } : undefined}
    >
      {/* ── EVENT DATES ─────────────────────────────────────────── */}
      <div className={rowClasses}>
        <Calendar
          className="w-5 h-5 flex-shrink-0"
          style={{ color: LABEL_YELLOW, marginTop: "0.15rem" }}
          aria-hidden="true"
        />
        <div className="text-center">
          <span
            data-testid="event-date-badge-kicker"
            className={
              (isRTL
                ? "text-[15px] sm:text-[17px] font-bold"
                : "text-[13px] sm:text-sm font-bold uppercase tracking-[0.18em]") +
              " block leading-tight"
            }
            style={
              isRTL
                ? { color: LABEL_YELLOW, fontFamily: AR_FONT, letterSpacing: "normal" }
                : { color: LABEL_YELLOW }
            }
          >
            {t("eventDatesKicker")}
          </span>
          <span
            data-testid="event-date-badge-value"
            className={
              (isRTL
                ? "text-[17px] sm:text-[19px] font-semibold"
                : "text-[15px] sm:text-[17px] font-semibold whitespace-nowrap") +
              " block leading-snug"
            }
            style={
              isRTL
                ? { color: VALUE_WHITE, fontFamily: AR_FONT, letterSpacing: "normal" }
                : { color: VALUE_WHITE }
            }
          >
            {t("eventDatesValue")}
          </span>
        </div>
      </div>

      {/* ── LOCATION ────────────────────────────────────────────── */}
      <div className={rowClasses}>
        <MapPin
          className="w-5 h-5 flex-shrink-0"
          style={{ color: LABEL_YELLOW, marginTop: "0.15rem" }}
          aria-hidden="true"
        />
        <div className="text-center">
          <span
            data-testid="event-date-badge-location-kicker"
            className={
              (isRTL
                ? "text-[15px] sm:text-[17px] font-bold"
                : "text-[13px] sm:text-sm font-bold uppercase tracking-[0.18em]") +
              " block leading-tight"
            }
            style={
              isRTL
                ? { color: LABEL_YELLOW, fontFamily: AR_FONT, letterSpacing: "normal" }
                : { color: LABEL_YELLOW }
            }
          >
            {t("eventLocationKicker")}
          </span>
          <span
            data-testid="event-date-badge-location"
            className={
              (isRTL
                ? "text-[16px] sm:text-[18px] font-semibold"
                : "text-[15px] sm:text-[17px] font-semibold") +
              " block leading-snug"
            }
            style={
              isRTL
                ? { color: VALUE_WHITE, fontFamily: AR_FONT, letterSpacing: "normal" }
                : { color: VALUE_WHITE }
            }
          >
            {t("eventDatesLocation")}
          </span>
        </div>
      </div>
    </div>
  );
}
